import { Link } from "react-router-dom";
import logo from "@/assets/logo.webp";
import { trackPhoneClick } from "@/lib/tracking";
import { SITE, SERVICES, servicePath } from "@/lib/site";

const Icon = ({ path, className = "h-4 w-4" }: { path: string; className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d={path} />
  </svg>
);

const PHONE_PATH = "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.77.62 2.6a2 2 0 0 1-.45 2.11L8 9.72a16 16 0 0 0 6.28 6.28l1.29-1.28a2 2 0 0 1 2.11-.45c.83.29 1.7.5 2.6.62A2 2 0 0 1 22 16.92Z";
const MAIL_PATH = "M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2ZM22 6l-10 7L2 6";
const MAP_PATH = "M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0ZM12 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z";
const CLOCK_PATH = "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 6v6l4 2";

const SiteFooter = () => {
  return (
    <footer className="bg-foreground text-primary-foreground">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="mb-3 flex items-center gap-2">
            <img src={logo} alt="MD Custom Construction" width={360} height={201} loading="lazy" decoding="async" className="h-10 w-auto" />
            <span className="font-heading text-base font-bold">MD Custom Construction</span>
          </div>
          <p className="text-sm text-primary-foreground/70">
            Customer service is our first priority. Trusted home remodeling and new construction in Southern Maryland for 10+ years.
          </p>
          <div className="mt-4 space-y-1 text-xs text-primary-foreground/60">
            <p>Licensed, Bonded &amp; Insured</p>
            {SITE.licenses.map((l) => <p key={l}>{l}</p>)}
          </div>
        </div>

        <div>
          <h3 className="mb-3 font-heading text-sm font-bold uppercase tracking-wider text-accent">Home Remodeling Services</h3>
          <ul className="space-y-2 text-sm">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link to={servicePath(s.slug)} className="text-primary-foreground/80 hover:text-accent">{s.name}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="mb-3 font-heading text-sm font-bold uppercase tracking-wider text-accent">Quick Links</h3>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about/" className="text-primary-foreground/80 hover:text-accent">About</Link></li>
            <li><Link to="/about/service-areas/" className="text-primary-foreground/80 hover:text-accent">Service Areas: Southern Maryland</Link></li>
            <li><Link to="/about/reviews/" className="text-primary-foreground/80 hover:text-accent">Reviews</Link></li>
            <li><Link to="/gallery/" className="text-primary-foreground/80 hover:text-accent">Gallery</Link></li>
            <li><Link to="/custom-home-builds/" className="text-primary-foreground/80 hover:text-accent">New Home Construction</Link></li>
            <li><Link to="/md-custom-construction-showroom/" className="text-primary-foreground/80 hover:text-accent">Showroom</Link></li>
            <li><Link to="/blog/" className="text-primary-foreground/80 hover:text-accent">Blog</Link></li>
            <li><Link to="/apply-now-for-financing/" className="text-primary-foreground/80 hover:text-accent">Apply Now for Financing</Link></li>
            <li><Link to="/contact/" className="text-primary-foreground/80 hover:text-accent">Contact</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="mb-3 font-heading text-sm font-bold uppercase tracking-wider text-accent">Get In Touch</h3>
          <ul className="space-y-3 text-sm text-primary-foreground/80">
            <li className="flex items-start gap-2">
              <Icon path={MAP_PATH} className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
              <span>{SITE.address.street}<br />{SITE.address.city}, {SITE.address.region} {SITE.address.postal}</span>
            </li>
            <li>
              <a href={SITE.phoneHref} onClick={trackPhoneClick} className="flex items-center gap-2 hover:text-accent">
                <Icon path={PHONE_PATH} className="h-4 w-4 text-accent" /> {SITE.phone}
              </a>
            </li>
            <li>
              <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 break-all hover:text-accent">
                <Icon path={MAIL_PATH} className="h-4 w-4 text-accent" /> {SITE.email}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Icon path={CLOCK_PATH} className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
              <span>Mon–Fri: 8:00 AM – 4:30 PM<br />Sat–Sun: Closed</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-primary-foreground/10">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-5 text-xs text-primary-foreground/50 md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} MD Custom Construction. All rights reserved.</p>
          <div className="flex flex-wrap gap-4">
            <Link to="/terms-of-use/" className="hover:text-accent">Terms of Use</Link>
            <Link to="/privacy-policy/" className="hover:text-accent">Privacy Policy</Link>
            <Link to="/sitemap/" className="hover:text-accent">Sitemap</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;